import { type RouteName } from '@app/constants/route.constants';
import {
  type PoliticallyExposedKind,
  type PoliticallyExposedFamilialRelation,
  type PoliticallyExposedWorkPosition,
} from '@features/AMLQuestions/PoliticallyExposedPerson/types';

export enum AccountStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  SUBMITTED = 'SUBMITTED',
  CANCELLED = 'CANCELLED',
  SENDING = 'SENDING',
  ESIGNATURE_REQUESTED = 'ESIGNATURE_REQUESTED',
  DOCUMENT_RECEIVED = 'DOCUMENT_RECEIVED',
  ESIGNATURE_READY = 'ESIGNATURE_READY',
  ESIGNATURE_SIGNED = 'ESIGNATURE_SIGNED',
  RECEIVED_SIGNED_DOCUMENTS = 'RECEIVED_SIGNED_DOCUMENTS',
  DOCUMENTS_ARCHIVED = 'DOCUMENTS_ARCHIVED',
  DECISION_SENT = 'DECISION_SENT',
  ERROR = 'ERROR',
  MANUAL = 'MANUAL',
  REJECTED = 'REJECTED',
  NEEDINFO = 'NEEDINFO',
}

export enum AccountType {
  SAVINGS_ACCOUNT = 'SAVINGS_ACCOUNT',
  SMART_ACCOUNT = 'SMART_ACCOUNT',
  CREDIT_CARD_ACCOUNT = 'CREDIT_CARD_ACCOUNT',
  CURRENT_ACCOUNT = 'CURRENT_ACCOUNT', // This is a current account without a card
  CURRENT_ACCOUNT_WITH_CARD = 'CURRENT_ACCOUNT_WITH_CARD',
  DEPOSIT_ACCOUNT = 'DEPOSIT_ACCOUNT',
  PLACEMENT_STANDARD_ACCOUNT = 'PLACEMENT_STANDARD_ACCOUNT',
  CONSUMER_LOAN = 'CONSUMER_LOAN',
  DEPOSIT_ACCOUNT_FIXED_RATE_3_MONTH = 'DEPOSIT_ACCOUNT_FIXED_RATE_3_MONTH',
  DEPOSIT_ACCOUNT_FIXED_RATE = 'DEPOSIT_ACCOUNT_FIXED_RATE',
  SAVINGS_ACCOUNT_HUSEIERNE = 'SAVINGS_ACCOUNT_HUSEIERNE',
  SAVINGS_ACCOUNT_HIGH_INTEREST_RATE_WITHOUT_CAMPAIGN = 'SAVINGS_ACCOUNT_HIGH_INTEREST_RATE_WITHOUT_CAMPAIGN',
  PLACEMENT_ACCOUNT_ORDINARY = 'PLACEMENT_ACCOUNT_ORDINARY',
  EMERGENCY_FUNDS_ORDINARY = 'BUFFER_ACCOUNT',
  HIGH_INTEREST_ACCOUNT_WEALTH = 'SAVINGS_ACCOUNT_HIGH_INTEREST_RATE_FORMUE',
  HIGH_INTEREST_ACCOUNT_FRAM = 'SAVINGS_ACCOUNT_HIGH_INTEREST_RATE_FRAM',
  CURRENT_ACCOUNT_PLATINUM_WITH_CARD = 'CURRENT_ACCOUNT_PLATINUM_WITH_CARD',
  CURRENT_ACCOUNT_PLATINUM_WITHOUT_CARD = 'CURRENT_ACCOUNT_PLATINUM_WITHOUT_CARD',
  CURRENT_ACCOUNT_PB_WITHOUT_CARD = 'CURRENT_ACCOUNT_PB_WITHOUT_CARD',
  CURRENT_ACCOUNT_PB_WITH_CARD = 'CURRENT_ACCOUNT_PB_WITH_CARD',
}

export enum BusinessPurpose {
  ACCOUNT_AND_CARD = 'ACCOUNT_AND_CARD',
  BUDGET = 'BUDGET',
  COMMERCIAL_SERVICES = 'COMMERCIAL_SERVICES',
  CREDIT = 'CREDIT',
  CRYPTO_CURRENCY = 'CRYPTO_CURRENCY',
  DEPOSIT = 'DEPOSIT',
  GUARANTOR = 'GUARANTOR',
  LOAN = 'LOAN',
  OTHER = 'OTHER',
  PENSION = 'PENSION',
  SALARY_ACCOUNT = 'SALARY_ACCOUNT',
  SAVINGS = 'SAVINGS',
  SECURITIES_TRADING = 'SECURITIES_TRADING',
  SINGLE_SERVICES = 'SINGLE_SERVICES',
}

export enum DwellingType {
  RENTAL = 'RENTAL',
  CONDOMINIUM = 'CONDOMINIUM',
  OWN_HOUSE = 'OWN_HOUSE',
  LIVING_WITH_PARENTS = 'LIVING_WITH_PARENTS',
  LODGER = 'LODGER',
}

export enum JobStatus {
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  RETIRED = 'RETIRED',
  UNEMPLOYED = 'UNEMPLOYED',
  STUDENT = 'STUDENT',
  PUBLIC_SECTOR = 'PUBLIC_SECTOR',
  PRIVATE_SECTOR = 'PRIVATE_SECTOR',
  DEPUTY = 'DEPUTY',
  DOMESTIC = 'DOMESTIC',
  INCAPABLE = 'INCAPABLE',
  REHABILITATION = 'REHABILITATION',
}

export enum CivilStatus {
  MARRIED = 'MARRIED',
  REGISTERED_PARTNER = 'REGISTERED_PARTNER',
  NOT_MARRIED = 'NOT_MARRIED',
  WIDOW = 'WIDOW',
  PARTNER = 'PARTNER',
  DIVORCED = 'DIVORCED',
  SEPARATED = 'SEPARATED',
  COHABITANT = 'COHABITANT',
}

export enum Employment {
  FULL_TIME = 'FULL_TIME',
  FULL_TIME_PUBLIC_SECTOR = 'FULL_TIME_PUBLIC_SECTOR',
  FULL_TIME_PRIVATE_SECTOR = 'FULL_TIME_PRIVATE_SECTOR',
  DISABILITY_BENEFIT_PROGRAMME = 'DISABILITY_BENEFIT_PROGRAMME',
  WORK_ASSESSMENT_ALLOWANCE = 'WORK_ASSESSMENT_ALLOWANCE',
  TEMPORARY = 'TEMPORARY',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  PENSIONER = 'PENSIONER',
  REHABILITATION = 'REHABILITATION',
  STUDENT = 'STUDENT',
  STAY_AT_HOME = 'STAY_AT_HOME',
}

export enum ApplicationStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  SUBMITTED = 'SUBMITTED',
  CANCELLED = 'CANCELLED',
  SENDING = 'SENDING',
  ESIGNATURE_REQUESTED = 'ESIGNATURE_REQUESTED',
  DOCUMENT_RECEIVED = 'DOCUMENT_RECEIVED',
  ESIGNATURE_READY = 'ESIGNATURE_READY',
  ESIGNATURE_SIGNED = 'ESIGNATURE_SIGNED',
  RECEIVED_SIGNED_DOCUMENTS = 'RECEIVED_SIGNED_DOCUMENTS',
  DOCUMENTS_ARCHIVED = 'DOCUMENTS_ARCHIVED',
  DECISION_SENT = 'DECISION_SENT',
  ERROR = 'ERROR',
  MANUAL = 'MANUAL',
  REJECTED = 'REJECTED',
  NEEDINFO = 'NEEDINFO',
  RETRY = 'RETRY',
}

export enum SigningOrder {
  OPEN_ACCOUNT = 'OPEN_ACCOUNT',
  CLOSE_ACCOUNT = 'CLOSE_ACCOUNT',
}

export enum TransfersFromOthersType {
  PERSON = 'PERSON',
  COMPANY = 'COMPANY',
}

export enum TransferReasonType {
  GIFT = 'INHERITANCE_OR_GIFTS',
  FAMILY = 'FAMILY',
  PURCHASE = 'INTERNET_PURCHASE',
  OTHER = 'OTHER',
}

export enum IncomeSource {
  SALARY = 'SALARY',
  PENSION = 'PENSION',
  SOCIAL_BENEFITS = 'SOCIAL_BENEFITS',
  INHERITANCE_OR_GIFTS = 'INHERITANCE_OR_GIFTS',
  REAL_ESTATE = 'REAL_ESTATE',
  INVESTMENT = 'INVESTMENT',
  CRYPTO_CURRENCY = 'CRYPTO_CURRENCY',
  COMMERCIAL_SERVICES = 'COMMERCIAL_SERVICES',
  RENTAL_INCOME = 'RENTAL_INCOME',
  STUDENT_SUPPORT = 'STUDENT_SUPPORT',
  OTHER = 'OTHER',
}

export enum CitizenshipSource {
  CUSTOMER = 'CUSTOMER',
  CUSTOMER_MASTER = 'CUSTOMER_MASTER',
}

interface SelectableContent<T> {
  selected: boolean | null;
  content: T;
}

export interface AccountToCreate {
  // TODO: Check that this should be a string since it could start with 0
  accountNumber?: number;
  accountStatus?: AccountStatus;
  accountType: AccountType;
  purpose?: BusinessPurpose;
}

export interface CashTransaction {
  quantity: number | null;
  selected: boolean;
  sum: number | null;
  transferReasons: TransferReason[];
}

export interface CryptoTradingContent {
  id: string;
  provider: string;
  currency: string;
  annualAmount: number;
}

export type CryptoTrading = SelectableContent<CryptoTradingContent | null>;

export interface OnBehalfOfOthersEntity {
  id: string;
  purpose: string | null;
  fullName: string;
  ninOrOrgNr: string | null;
  relation: string;
  email: string;
  phoneNumber: string;
}

export type OnBehalfOfOthers = SelectableContent<OnBehalfOfOthersEntity[]>;

export interface TransferReason {
  reasonType: TransferReasonType;
  reasonName: string | null;
}

export interface Income {
  sourceOfIncome?: IncomeSource;
  otherSourceOfIncome?: string | null;
  income: number | null;
}

export interface InternationalTransfer {
  country: string;
  sum: number | null;
  quantity: number | null;
  transferReasons: TransferReason[];
}

export interface TaxLiability {
  taxCountry: string;
  taxId: string;
}

export type TaxLiabilities = SelectableContent<TaxLiability[]>;

export type InternationalTransfers = SelectableContent<InternationalTransfer[]>;

export interface PoliticallyExposedRelation {
  pepRelation?: PoliticallyExposedKind;
  pepWorkPosition?: PoliticallyExposedWorkPosition;
  pepRelationDetails?: PoliticallyExposedFamilialRelation | string;
}

export type PoliticallyExposed = SelectableContent<PoliticallyExposedRelation[]>;

export interface AmlInformation {
  cashDeposit?: CashTransaction;
  cashWithdrawals?: CashTransaction;
  citizenship?: string | null;
  cryptoTrading?: CryptoTrading;
  incomes?: Income[];
  onBehalfOfOthers?: OnBehalfOfOthers;
  otherPurpose?: string | null;
  otherTaxLiabilities?: TaxLiabilities;
  politicallyExposed?: PoliticallyExposed;
  purposes?: BusinessPurpose[];
  reasonForPep?: string | null;
  transfersAbroad?: InternationalTransfers;
  transfersFromAbroad?: InternationalTransfers;
  transfersFromOthers?: boolean | null;
  transfersFromOthersNumber?: number | null;
  transfersFromOthersType?: TransfersFromOthersType | null;
  usaTaxLiability?: TaxLiabilities;
  willUseAsPrimaryBank?: boolean | null;
}

export interface Citizenship {
  countryCode: string;
  countryName: string;
  source?: CitizenshipSource;
}

export interface CustomerProperties {
  amlInfo: AmlInformation | null;
  citizenships?: Citizenship[];
  city: string;
  civilStatus?: CivilStatus;
  consentStatus: boolean;
  dwelling: DwellingType;
  email?: string;
  employer: string;
  employmentDate: string;
  employmentType: Employment;
  firstName: string;
  jobStatus?: JobStatus;
  lastName: string;
  mobileNumber?: string;
  nin: string;
  occupation: string;
  postalCode: string;
  streetAddress: string;
  containsNonValidCreditRemarks: boolean;
  containsDebtNegotiations: boolean;
  currentUIState?: RouteName;
}

export interface SigningDetails {
  signingOrderType: SigningOrder;
  signingUrl: string;
  taskReference: string;
}
export interface SigningInputModel {
  eidCancelUrl: string;
  eidExitUrl: string;
  language: string;
}

// Request model
export interface CustomerAccountCommand {
  accounts: AccountToCreate[];
  applicationId?: string;
  customer?: CustomerProperties;
  originReference?: string;
  signingInputModel?: SigningInputModel;
  coreApplicationId?: string;
}

// Response model
export interface CustomerAccountProperties extends CustomerAccountCommand {
  applicationId: string;
  customer: CustomerProperties;
  applicationStatus: ApplicationStatus;
  accounts: AccountToCreate[];
  signingDetails?: SigningDetails;
  originReference?: string;
  isBankCustomer: boolean;
  updatedDate: string;
  createdDate: string;
  currentUIState: RouteName;
}

export interface Consents {
  electronicSolicitation: boolean;
  socialMediaSolicitation: boolean;
  enterprise?: boolean;
  electronicCustomer: boolean;
  triggerClient: string;
  emailPrivate?: string;
  mobilePrivate?: string;
  focus?: boolean;
}

export interface Account {
  accountName: string;
  accountNumber: string;
  aliasName: string;
  currency: string;
  bookBalance: number;
  productCode: string;
  accountStatusDescription: string;
  accountTypeDescription: string;
}
